/**
 * MODULE_REGISTRY — the single contract for the journey module system.
 *
 * Adding a new entry type = create one component file + add one line here.
 * It then works automatically in BOTH:
 *   - the client renderer (JourneyShell looks up component by entry.type)
 *   - the team authoring form (JourneyAdminModal renders authoringFields)
 *
 * Each module entry:
 *   component       — React component, receives the uniform props contract:
 *                     { entry, project, response, onRespond(kind, payload), isActive }
 *   label           — human label (type picker + chips)
 *   icon            — short glyph for the authoring list
 *   usesMilestone   — true if this type embeds a milestone (shows the milestone picker)
 *   nodeSummary(e)  — optional short string for the authoring outline node (what it links)
 *   stub            — true = registered for authoring but client component not built yet
 *   authoringFields — fields the editor renders to edit entry.data:
 *                     { key, label, type: 'text'|'textarea'|'markdown'|'url'|'list', placeholder? }
 */

import InstructionEntry from './InstructionEntry.jsx'
import MilestoneEntry from './MilestoneEntry.jsx'
import ApprovalEntry from './ApprovalEntry.jsx'
import HomeworkEntry from './HomeworkEntry.jsx'
import PresentationEntry from './PresentationEntry.jsx'
import ScheduleApprovalEntry from './ScheduleApprovalEntry.jsx'
import SketchesEntry from './SketchesEntry.jsx'
import DocumentEntry from './DocumentEntry.jsx'
import WalkthroughEntry from './WalkthroughEntry.jsx'
import ClientReleaseEntry from './ClientReleaseEntry.jsx'
import OnboardingQuestionnaireEntry from './OnboardingQuestionnaireEntry.jsx'
import ProposalReviewEntry from './ProposalReviewEntry.jsx'
import DraftProposalPreviewEntry from './DraftProposalPreviewEntry.jsx'
import AsrReviewEntry from './AsrReviewEntry.jsx'
import MeetingMinutesEntry from './MeetingMinutesEntry.jsx'
import PunchListEntry from './PunchListEntry.jsx'
import ReferenceReviewEntry from './ReferenceReviewEntry.jsx'
import ContactCardEntry from './ContactCardEntry.jsx'
import PermittingEntry from './PermittingEntry.jsx'
import HearingApprovalEntry from './HearingApprovalEntry.jsx'
import ConsultantProposalsEntry from './ConsultantProposalsEntry.jsx'
import ProviderRequestEntry from './ProviderRequestEntry.jsx'
import NeighborLetterEntry from './NeighborLetterEntry.jsx'

export const MODULE_REGISTRY = {
  instruction: {
    component: InstructionEntry,
    label: 'Instruction',
    icon: '✎',
    authoringFields: [
      { key: 'body', label: 'Message (markdown)', type: 'markdown', placeholder: 'Write the message the client should read…' },
    ],
  },
  milestone: {
    component: MilestoneEntry,
    label: 'Milestone & files',
    icon: '◆',
    usesMilestone: true,
    nodeSummary: (e) => e.milestone_type || (e.milestone && e.milestone.name) || 'Milestone',
    authoringFields: [
      { key: 'intro', label: 'Lead-in text (optional, markdown)', type: 'markdown', placeholder: 'A short note shown above the files…' },
    ],
  },
  approval: {
    component: ApprovalEntry,
    label: 'Approval',
    icon: '✓',
    authoringFields: [
      { key: 'intro', label: 'Description (markdown)', type: 'markdown', placeholder: 'Describe what the client is approving…' },
      { key: 'imageUrl', label: 'Image URL (optional)', type: 'url', placeholder: 'https://…' },
      { key: 'optionLabel', label: 'Approve button suffix (optional)', type: 'text', placeholder: 'e.g. this finish' },
    ],
  },
  presentation: {
    component: PresentationEntry,
    label: 'Presentation',
    icon: '❖',
    usesMilestone: true,
    nodeSummary: (e) => e.milestone_type || (e.milestone && e.milestone.name) || 'Presentation',
    authoringFields: [
      { key: 'intro', label: 'Lead-in text (optional, markdown)', type: 'markdown', placeholder: 'A short note shown before the client opens the presentation…' },
    ],
  },

  // Meeting minutes — client-facing surface for a posted meeting's minutes PDF. Created from
  // PostMinutesModal.jsx's "Client Hub" prompt at post time, OR manually via the generic "Add
  // entry" modal's meeting picker (usesMeeting — for backfilling a meeting
  // that was posted before this feature existed, or skipped at post time). Phase/date for sorting
  // are snapshotted into entry.data at creation/edit time (matchPhaseName in services/phases.js
  // normalizes Meetings' shorthand Phase value to the project's real Phase Name) — not
  // milestone-anchored, so unlike usesMilestone these aren't live-resolved from the record.
  meeting_minutes: {
    component: MeetingMinutesEntry,
    label: 'Meeting Minutes',
    icon: '🗒',
    usesMeeting: true,
    nodeSummary: (e) => (e.meeting && e.meeting.type) || 'Meeting minutes',
    authoringFields: [
      { key: 'intro', label: 'Lead-in text (optional, markdown)', type: 'markdown', placeholder: 'A short note shown above the minutes…' },
    ],
  },

  // Sketch ribbon — incremental drawings/renderings shown as a swipeable bar (not a full package,
  // not tied to a milestone). Lives only on this journey entry; hidden until the entry is published.
  sketches: {
    component: SketchesEntry,
    label: 'Sketch ribbon',
    icon: '❏',
    nodeSummary: (e) => { const n = (e.data && Array.isArray(e.data.sketches)) ? e.data.sketches.length : 0; return n ? n + ' sketch' + (n === 1 ? '' : 'es') : 'Sketch ribbon' },
    authoringFields: [
      { key: 'intro', label: 'Lead-in text (optional, markdown)', type: 'markdown', placeholder: 'A short note shown above the sketches…' },
      { key: 'sketches', label: 'Sketches', type: 'sketches' },
    ],
  },

  // PDF document — one whole PDF sent as it is (docs/CLIENT_HUB_PDF_DOCUMENTS.md, Matt 2026-10-29):
  // the sketch ribbon's sibling for a multi-page document. Placed by phase + date; filed under a
  // milestone (data.milestoneId) when added from that milestone's Documents.
  document: {
    component: DocumentEntry,
    label: 'PDF document',
    icon: '▤',
    nodeSummary: (e) => { const p = (e.data && e.data.pdf) || null; return p && p.url ? (p.pages ? p.pages + ' page' + (p.pages === 1 ? '' : 's') + ' · PDF' : 'PDF') : 'No PDF yet' },
    authoringFields: [
      { key: 'intro', label: 'Lead-in text (optional, markdown)', type: 'markdown', placeholder: 'A short note shown above the document…' },
      { key: 'pdf', label: 'PDF', type: 'pdfDocument' },
    ],
  },

  // Model walkthrough — the 3D model as it stood when published (Matt, 2026-10-15). Publishing the
  // entry releases it: the model is frozen server-side (api/_walkthroughRelease.js) and the client
  // gets a "Walk the model" button into the full background viewer.
  walkthrough: {
    component: WalkthroughEntry,
    label: 'Model walkthrough',
    icon: '⬢',
    nodeSummary: (e) => (e.data && e.data.releasedAt) ? 'Released ' + new Date(e.data.releasedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Model walkthrough',
    authoringFields: [
      { key: 'intro', label: 'Lead-in text (optional, markdown)', type: 'markdown', placeholder: 'A short note shown above the button…' },
      { key: 'modelId', label: 'Model', type: 'walkthroughModel' },
    ],
  },

  // A hearing approval (api/_hearings.js, docs/HEARING_OUTCOMES.md): posted by the Permitting
  // Coordinator's hearing card the morning after the hearing, never added here. The appeal status
  // updates in place when the appeal period ends.
  hearing_approval: {
    component: HearingApprovalEntry,
    label: 'Hearing approval',
    icon: '✦',
    nodeSummary: (e) => [e.data && e.data.bodyName, e.data && e.data.appealStatus === 'none' ? 'final' : e.data && e.data.appealEndsOn ? 'appeal period to ' + e.data.appealEndsOn : ''].filter(Boolean).join(' · '),
    authoringFields: [],
  },

  // A consultant's proposal (or several quotes for one discipline) for the owner to approve, posted by
  // the RFP sender's review in triage, never added here (api/_consultantProposals.js, docs/TEAM.md
  // "Proposals that file themselves"). The owner approves one and uploads their signed copy.
  consultant_proposals: {
    component: ConsultantProposalsEntry,
    label: 'Consultant proposal',
    icon: '§',
    nodeSummary: (e) => {
      const c = e.consultantProposals || {}
      const ps = c.proposals || []
      const chosen = ps.find(p => p.id === c.chosenId)
      if (chosen) return chosen.firm + (chosen.signed ? ' · signed' : ' · approved, signed copy to come')
      return ps.length === 1 ? ps[0].firm + ' · waiting on the owner' : ps.length + ' to choose from'
    },
    authoringFields: [],
  },

  // Neighbor letters (docs/NEIGHBOR_LETTERS.md, 2026-11-03): the window/door input form or a letter of
  // support for a hearing, posted from the agency review's "Neighbor letter…", never added here. The
  // client uploads each signed letter; it files as a Stakeholder Input on the review.
  neighbor_letter: {
    component: NeighborLetterEntry,
    label: 'Neighbor letters',
    icon: '✉',
    nodeSummary: (e) => {
      const n = (e.neighborLetter && e.neighborLetter.returned || []).length
      const what = e.data && e.data.kind === 'support' ? 'Letter of support' : 'Input form'
      return what + ' · ' + (n ? n + ' returned' : 'none returned yet')
    },
    authoringFields: [
      { key: 'note', label: 'Note to the client (optional, markdown): which neighbors to ask', type: 'markdown' },
    ],
  },

  // Client Hub release from AlderCAD (api/_clientRelease.js): the plans + the frozen model, posted
  // from AlderCAD's Releases window, never added here (pushed from where it lives). Editing here
  // changes only the title, note and placement; the drawings and model are fixed at release.
  client_release: {
    component: ClientReleaseEntry,
    label: 'Plans & 3D model',
    icon: '▦',
    nodeSummary: (e) => {
      const n = (e.data && e.data.drawings || []).length
      return e.data && e.data.status === 'generating' ? 'Being prepared' : n + ' drawing' + (n === 1 ? '' : 's') + ' + 3D model'
    },
    authoringFields: [
      { key: 'intro', label: 'Note to the client (optional, markdown)', type: 'markdown', placeholder: 'What changed, and what to look at…' },
    ],
  },

  // Site survey / as-built requests (docs/OWNER_PROVIDER_REQUESTS.md, 2026-11-02): homework that
  // helps the owner ORDER one. Surveyors / as-built firms serving the project's county, sent in the
  // owner's name; proposals go to the owner. Alder James never orders or reviews these. The as-built
  // card is for remodels and additions only (never New Build) where our proposal leaves out the
  // existing building 3D model. Provider list, acknowledgement and email: api/_ownerProviderRequest.js.
  survey_request: {
    component: ProviderRequestEntry,
    label: 'Order a site survey',
    icon: '⌖',
    nodeSummary: (e) => { const n = (e.providerRequest && e.providerRequest.requests || []).length; return n ? 'Sent to ' + n : 'Not sent yet' },
    authoringFields: [
      { key: 'intro', label: 'Why it is needed (optional, markdown; a standard line is used when blank)', type: 'markdown' },
    ],
  },
  asbuilt_request: {
    component: ProviderRequestEntry,
    label: 'Order as-built drawings',
    icon: '⌗',
    nodeSummary: (e) => { const n = (e.providerRequest && e.providerRequest.requests || []).length; return n ? 'Sent to ' + n : 'Not sent yet' },
    authoringFields: [
      { key: 'intro', label: 'Why it is needed (optional, markdown; a standard line is used when blank)', type: 'markdown' },
    ],
  },

  // Homework — free-form task not tied to a coordination item (kept for ad-hoc asks)
  homework: {
    component: HomeworkEntry,
    label: 'Homework (free-form)',
    icon: '☐',
    authoringFields: [
      { key: 'body', label: 'Task (markdown)', type: 'markdown', placeholder: 'e.g. Please obtain a topographic survey from a licensed surveyor.' },
      { key: 'dueText', label: 'Timing note (optional)', type: 'text', placeholder: 'e.g. Needed before Schematic Design' },
    ],
  },

  schedule_approval: {
    component: ScheduleApprovalEntry,
    label: 'Schedule approval',
    icon: '▦',
    usesMilestone: true,
    nodeSummary: (e) => (e.milestone_type ? e.milestone_type + ' schedule' : 'Schedule') + (e.schedule ? ' · v' + (e.schedule.release?.item_count ?? '') : ''),
    authoringFields: [
      { key: 'intro', label: 'Lead-in text (optional, markdown)', type: 'markdown', placeholder: 'A short note shown above the schedule the client is approving…' },
    ],
  },

  // Onboarding questionnaire — the client's pre-design intake (first homework). On submit it
  // creates/updates the project's Owner Criteria record and fires the seed-concepts automation.
  'onboarding-questionnaire': {
    component: OnboardingQuestionnaireEntry,
    label: 'Onboarding questionnaire',
    icon: '❑',
    authoringFields: [
      { key: 'intro', label: 'Lead-in text (optional, markdown)', type: 'markdown', placeholder: 'A short note shown before the client begins the questionnaire…' },
    ],
  },

  // Proposal — client reviews + signs their proposal in the journey (published by "Post & send"
  // from the CRM). entry.data carries { proposalId, proposalType, contractFee, retainer, tiered,
  // packages, pdfUrl, proposalDate, intro }. Signing writes Accepted + Selected Package back to
  // the Fee Proposal.
  proposal: {
    component: ProposalReviewEntry,
    label: 'Proposal to sign',
    icon: '✍',
    nodeSummary: (e) => (e.data && e.data.proposalType) || 'Proposal',
    authoringFields: [
      { key: 'intro', label: 'Cover note (markdown)', type: 'markdown' },
    ],
  },
  // Draft proposal preview — team-only, synthetic (never authored, never persisted, never seen by
  // a real client). JourneyPreviewOverlay.jsx injects one entry of this type when its own "Show
  // draft proposal" toggle is on. No authoringFields — there is nothing to author.
  draft_proposal_preview: {
    component: DraftProposalPreviewEntry,
    label: 'Draft proposal (preview only)',
    icon: '📝',
  },
  // ASR — client reviews + e-signs an Additional Services Request (published by "Post & send" from
  // the CRM). entry.data: { asrId, number, title, totalFee, feeType, stipulations, pdfUrl, intro }.
  asr_review: {
    component: AsrReviewEntry,
    label: 'Additional Services Request',
    icon: '＋',
    nodeSummary: (e) => (e.data && (e.data.title || ('ASR No. ' + (e.data.number || '')))) || 'ASR',
    authoringFields: [
      { key: 'intro', label: 'Cover note (markdown)', type: 'markdown' },
    ],
  },

  // Punch list — view/comment-only (Construction Gates Phase D trimmings). Project-scoped, not
  // milestone-linked: api/_shareDocs.js#fetchProjectPunchItems attaches entry.punchItems whenever a
  // punch_list entry exists. Owners never control punch status, only leave a comment.
  punch_list: {
    component: PunchListEntry,
    label: 'Punch List',
    icon: '☰',
    nodeSummary: (e) => { const n = Array.isArray(e.punchItems) ? e.punchItems.length : 0; return n ? n + ' item' + (n === 1 ? '' : 's') : 'Punch list' },
    authoringFields: [
      { key: 'intro', label: 'Lead-in text (optional, markdown)', type: 'markdown', placeholder: 'A short note shown above the punch list…' },
    ],
  },

  // References — a letter-format PDF of past clients who've given permission to be contacted by
  // prospects (docs/REFERENCES.md), released by "Post & release" from the Opportunity's References
  // section, independently of proposal status (before or after a proposal, or with no proposal at
  // all). View/download only, no signing/response. entry.data: { referenceId, title, pdfUrl, intro }.
  reference: {
    component: ReferenceReviewEntry,
    label: 'Client References',
    icon: '☏',
    nodeSummary: (e) => (e.data && e.data.title) || 'Client References',
    authoringFields: [
      { key: 'intro', label: 'Cover note (markdown)', type: 'markdown' },
    ],
  },

  // Permitting — one living card per project, compiled live from the Permits section's activity log
  // (api/_permittingJourney.js attaches entry.permitting). Created and bumped automatically whenever
  // a permit activity is logged, by hand or by posting (docs/OPERATING_MODEL.md, build step 4 slice
  // B). Nothing to author.
  permitting: {
    component: PermittingEntry,
    label: 'Permitting',
    icon: '⚖',
    nodeSummary: (e) => { const n = e.permitting && Array.isArray(e.permitting.reviews) ? e.permitting.reviews.length : 0; return n ? n + ' agency review' + (n === 1 ? '' : 's') : 'Permitting' },
  },

  // Share a contact (Matt, 2026-10-15): a person the client should have on file (a consultant, a
  // contractor, an agency planner), with the team's note on why. The contact is snapshotted into
  // entry.data.contact when authored; the client gets tap-to-call/email and a vCard download, and
  // publishing emails subscribers with the vCard attached (api/journey-admin.js#notifyEntryPublished).
  contact_card: {
    component: ContactCardEntry,
    label: 'Share a contact',
    icon: '☺',
    nodeSummary: (e) => { const d = e.data || {}; return (d.kind === 'org' && d.org && d.org.name) || (d.contact && (d.contact.name || d.contact.company)) || 'Contact' },
    authoringFields: [
      { key: 'contact', label: 'Contact', type: 'contactCard' },
      { key: 'intro', label: 'Message (markdown)', type: 'markdown', placeholder: 'Why you are sharing this contact, e.g. "This is our structural engineer. Expect a call from her office about the site visit."' },
    ],
  },

  selections: {
    component: null,
    stub: true,
    label: 'Product selections',
    icon: '◧',
    authoringFields: [
      { key: 'intro', label: 'Intro (markdown)', type: 'markdown' },
    ],
  },
}

export const MODULE_TYPES = Object.keys(MODULE_REGISTRY)

export function getModule(type) {
  return MODULE_REGISTRY[type] || null
}
